import React, { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import * as notify from "../../../shared/notify";
import eventEmitter from "../../../shared/events";

import LoaderButton from "../../loader/loader-button";

import { completeShipment, deleteShipment, getProductLimits } from "../lib/shipmentData";
import { ShipmentContext } from "../contexts";
import { ViewOnlyPermissionsContext } from "contexts/view-only-permissions/view-only-permissions";
import { dialogConfirm } from "../../../shared/dialog";
import { OverrideDates } from "./override-dates";
import { SHIPMENT_TYPE_MAP } from "../../shipments/lib/constants";
import { useNavigate } from "react-router-dom";

const UtilityNav = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [shipmentInfoSaved, setShipmentInfoSaved] = useState(true);
    const { bins, hasProducts, shipmentData } = useContext(ShipmentContext);
    const { viewOnlyShipment } = useContext(ViewOnlyPermissionsContext);

    useEffect(() => {
        eventEmitter.on("COMPLETE_SHIPMENT", handleCompleteEvent);
        eventEmitter.on("SHIPMENT_INFO_SAVED", updateShipmentInfoSaved);

        return () => {
            eventEmitter.removeListener("COMPLETE_SHIPMENT", handleCompleteEvent);
            eventEmitter.removeListener("SHIPMENT_INFO_SAVED", updateShipmentInfoSaved);
        };
    }, [bins, hasProducts, shipmentData]);

    const updateShipmentInfoSaved = (value) => {
        setShipmentInfoSaved(value);
    };

    const handleCompleteEvent = () => {
        onComplete(false);
    };

    const onComplete = async (e) => {
        let overrideSlaLimits = false;

        if (e) {
            e.preventDefault();
        }

        const { valid, override, cancel } = await validate();

        if (cancel) {
            return;
        }

        if (override) {
            overrideSlaLimits = true;
        }

        setIsSubmitting(true);

        const response = await completeShipment(shipmentData.key, valid && overrideSlaLimits);

        if (notify.errorResponse(response)) {
            const errorFields = response.errors.map((err) => {
                return err.field;
            });
            errorFields.forEach((field, i) => {
                if (!field) {
                    return;
                }

                const $field = document.getElementById(field.charAt(0).toLowerCase() + field.slice(1));

                if ($field) {
                    $field.classList.add("-error");

                    if (i === 0) {
                        $field.focus();
                    }
                }
            });
            setIsSubmitting(false);

            return;
        }

        navigate(`/bin-label/${shipmentData.key}/delay`);
    };

    const onDelete = async (e) => {
        e.preventDefault();
        const confirm = await notify.confirm("Are you sure you want to delete this shipment?");

        if (!confirm) {
            return;
        }

        const response = await deleteShipment(shipmentData.key);

        if (!response) {
            return;
        }

        notify.success(`Shipment Deleted`);
        navigate("/shipments");
    };

    const printBinLabel = () => {
        navigate(`/bin-label/${shipmentData.key}`);
    };

    const validate = async () => {
        if (bins.length < 1) {
            notify.error("A shipment requires at least 1 bin.");

            return {
                valid: false,
            };
        }

        if (!hasProducts) {
            notify.error("A shipment requires at least 1 upc.");

            return {
                valid: false,
            };
        }

        const productLimits = await getProductLimits(shipmentData.key);

        if (productLimits.isClientLimitExceeded) {
            const confirmChange = await dialogConfirm({
                data: {
                    suggested: productLimits.availableShipmentStartDate,
                },
                Component: OverrideDates,
            });

            if (confirmChange.cancel) {
                return {
                    valid: true,
                    cancel: confirmChange.cancel,
                };
            }

            return {
                valid: true,
                override: confirmChange.update,
            };
        }

        return {
            valid: true,
            override: false,
        };
    };

    const titlePrefix = shipmentData.shipmentType !== 0 ? `${SHIPMENT_TYPE_MAP[shipmentData.shipmentType].label} ` : "";

    return (
        <div>
            {shipmentData.isCompleted ? (
                <div className="button-group">
                    <h1>{titlePrefix}Shipment Review</h1>
                    <a
                        href={`/shipment-status/${shipmentData.key}`}
                        title="Shipment Status"
                        className="button"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FontAwesomeIcon icon={faInfoCircle} />
                    </a>
                    <LoaderButton
                        className="button -error -right"
                        isSubmitting={isSubmitting}
                        onClick={onDelete}
                        text="Delete Shipment"
                        disabled={viewOnlyShipment.deleteShipment}
                    />
                    <LoaderButton
                        className="button -primary -right"
                        isSubmitting={isSubmitting}
                        onClick={printBinLabel}
                        text="Reprint Bin Labels"
                    />
                </div>
            ) : (
                <div className="button-group">
                    <h1>{titlePrefix}Shipment Editor</h1>
                    <LoaderButton
                        className="button -error -right"
                        isSubmitting={isSubmitting}
                        onClick={onDelete}
                        text="Delete Shipment"
                        disabled={viewOnlyShipment.deleteShipment}
                    />
                    <LoaderButton
                        className="button -primary -right"
                        isSubmitting={!shipmentInfoSaved || isSubmitting}
                        onClick={onComplete}
                        text="Complete Shipment"
                    />
                </div>
            )}
        </div>
    );
};

export default UtilityNav;
